import { SecureKeys } from '@heviton/bridge-protocol';
import { useAuthStore } from '@/entities/auth';
import { isNative } from '@/shared/bridge';

export interface AuthSyncCallbacks {
  /** 다른 탭이 로그아웃(토큰 삭제)했을 때 — 이 탭의 세션·화면도 정리한다. */
  onRemoteSignOut: () => void;
}

/**
 * 탭 간 auth 동기화 — auth store 는 zustand `persist` 로 localStorage 에 있지만, **hydration 은 부팅 때
 * 1회**뿐이라 그 뒤 다른 탭이 토큰을 갱신해도 이 탭의 메모리 상태는 옛 토큰 그대로다.
 *
 * # 왜 필요한가 — refresh rotation 이 탭 간에 충돌한다
 * refresh 는 **첫 호출만 유효**(rotation). 탭 두 개가 같은 refresh 토큰 R1 을 들고 있다가
 *   ① 탭 A 가 새로고침/요청 → 401 → R1 로 재발급 → R2 저장 (A 는 정상)
 *   ② 탭 B 는 여전히 메모리에 R1 → 401 → R1 로 재발급 시도 → BE 거부(이미 소모됨)
 *   ③ B 의 `onAuthError` 가 `clear()` → **B 로그아웃 + localStorage 삭제 → A 도 다음 부팅에 로그아웃**
 * 즉 한쪽 탭의 정상 갱신이 다른 탭을 끊는다. 저장소를 다시 읽어오면 ②가 R2 로 진행돼 사슬이 끊긴다.
 *
 * # 어떻게
 * `storage` 이벤트는 **변경을 일으킨 탭 외 다른 탭에서만** 발화한다 — 자기 쓰기에 자기가 반응하는 루프가
 * 원천적으로 없다. 그래서 별도 echo 가드 없이 곧바로 `persist.rehydrate()` 로 저장소 값을 메모리에 싣는다.
 *   - `newValue !== null` = 다른 탭이 토큰 갱신 → rehydrate(메모리 최신화)
 *   - `newValue === null` = 다른 탭이 로그아웃(`clearStorage`) → `onRemoteSignOut`
 *   - `key === null` = `localStorage.clear()`(전체 삭제) → 우리 키도 날아간 것으로 취급
 *
 * ⚠️ **native(WebView) 는 배선하지 않는다** — 저장소가 localStorage 가 아니라 bridge secure storage 라
 * `storage` 이벤트 자체가 없고, 단일 WebView 라 탭 경합도 성립하지 않는다.
 *
 * rehydrate 만으로 못 막는 **동시 발화**(두 탭이 같은 순간 401 → 둘 다 R1 로 재발급)는 이벤트가 늦게 오므로
 * composition 의 공통 `refreshToken` 이 실패 시 저장소를 다시 읽어 한 번 더 구제한다(2중 방어).
 */
export function initAuthSync({ onRemoteSignOut }: AuthSyncCallbacks): void {
  if (isNative()) return;

  window.addEventListener('storage', (event) => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== SecureKeys.auth) return;

    if (event.newValue === null) {
      onRemoteSignOut();
      return;
    }
    void useAuthStore.persist.rehydrate();
  });
}
